import React from 'react';
import { Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer';
import { MaintenanceItem, Vehicle } from '../../types';

const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v).trim();
  if (s === '') return 0;
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s) || 0;
};

const formatCurrency = (value: unknown): string =>
  toNum(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const truncate = (v: unknown, max: number): string => {
  const s = String(v ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
};

const breakLongWords = (text: string, chunkSize = 24): string => {
  if (!text) return text;
  return text
    .split(' ')
    .map((token) => {
      if (token.length <= chunkSize) return token;
      const parts: string[] = [];
      for (let i = 0; i < token.length; i += chunkSize) parts.push(token.slice(i, i + chunkSize));
      return parts.join(' ');
    })
    .join(' ');
};

const styles = StyleSheet.create({
  page: { padding: 32, fontSize: 10, fontFamily: 'Helvetica', color: '#111827' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14, gap: 12, alignItems: 'center' },
  logo: { width: 140, height: 36, objectFit: 'contain' },
  title: { fontSize: 16, fontWeight: 700 },
  subtitle: { marginTop: 4, color: '#6B7280' },
  vehicleBox: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, padding: 10, marginBottom: 14 },
  vehicleTop: { flexDirection: 'row', gap: 10 },
  vehicleImg: { width: 140, height: 90, objectFit: 'cover', borderRadius: 6 },
  vehicleImgPlaceholder: { width: 140, height: 90, borderRadius: 6, backgroundColor: '#F3F4F6' },
  vehicleInfo: { flexGrow: 1 },
  vehicleName: { fontSize: 12, fontWeight: 700 },
  vehicleMeta: { marginTop: 4, color: '#374151' },
  totalsRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#E5E7EB' },
  totalsLabel: { color: '#6B7280' },
  totalsValue: { fontWeight: 700 },
  table: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, overflow: 'hidden' },
  rowHeader: { flexDirection: 'row', backgroundColor: '#F9FAFB', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingVertical: 8, paddingHorizontal: 8 },
  row: { flexDirection: 'column', borderBottomWidth: 1, borderBottomColor: '#F3F4F6', paddingVertical: 7, paddingHorizontal: 8 },
  rowTop: { flexDirection: 'row', alignItems: 'flex-start' },
  cellStatus: { width: 80 },
  cellPriority: { width: 70 },
  cellEst: { width: 90, textAlign: 'right', fontWeight: 700 },
  descText: { fontSize: 10, lineHeight: 1.25 },
  noteText: { fontSize: 9, lineHeight: 1.2, color: '#6B7280' },
});

export interface VehicleMaintenanceFuturePDFProps {
  vehicle: Vehicle;
  items: MaintenanceItem[];
  generatedAtLabel: string;
  vehicleImageUrl?: string | null;
  logoUrl?: string | null;
}

const getVehicleIdLabel = (v: Vehicle): string => {
  if ((v as any).identification) return String((v as any).identification);
  if ((v as any).no_plate) return 'Sem placa';
  return String((v as any).plate || '—');
};

const statusLabel = (s: MaintenanceItem['status']) => {
  switch (s) {
    case 'pending': return 'Pendente';
    case 'in_progress': return 'Em andamento';
    case 'done': return 'Concluído';
    case 'cancelled': return 'Cancelado';
    default: return String(s);
  }
};

const priorityLabel = (p: MaintenanceItem['priority']) => {
  switch (p) {
    case 'low': return 'Baixa';
    case 'medium': return 'Média';
    case 'high': return 'Alta';
    default: return String(p);
  }
};

const VehicleMaintenanceFuturePDF: React.FC<VehicleMaintenanceFuturePDFProps> = ({ vehicle, items, generatedAtLabel, vehicleImageUrl, logoUrl }) => {
  const totalEstimated = items.reduce((sum, it) => sum + (it.estimated_amount != null ? toNum(it.estimated_amount) : 0), 0);
  const sorted = [...items].sort((a, b) => String(a.status).localeCompare(String(b.status)));

  return (
    <Document title="Manutenções futuras">
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : <View />}
          <View>
            <Text style={styles.title}>Manutenções futuras</Text>
            <Text style={styles.subtitle}>Gerado em: {generatedAtLabel}</Text>
          </View>
          <View>
            <Text style={styles.subtitle}>Total itens: {sorted.length}</Text>
          </View>
        </View>

        <View style={styles.vehicleBox}>
          <View style={styles.vehicleTop}>
            {vehicleImageUrl ? <Image src={vehicleImageUrl} style={styles.vehicleImg} /> : <View style={styles.vehicleImgPlaceholder} />}
            <View style={styles.vehicleInfo}>
              <Text style={styles.vehicleName}>
                {vehicle.brand} {vehicle.model} ({vehicle.year})
              </Text>
              <Text style={styles.vehicleMeta}>Identificação: {getVehicleIdLabel(vehicle)}</Text>
              <Text style={styles.vehicleMeta}>Cor: {vehicle.color || '—'}</Text>
              {vehicle.internal_notes ? <Text style={styles.vehicleMeta}>Obs. internas: {truncate(vehicle.internal_notes, 120)}</Text> : null}
            </View>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Total estimado:</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalEstimated)}</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.rowHeader}>
            <Text style={styles.cellStatus}>Status</Text>
            <Text style={styles.cellPriority}>Prior.</Text>
            <Text style={{ flexGrow: 1 }}>Descrição</Text>
            <Text style={styles.cellEst}>Estimativa</Text>
          </View>

          {sorted.map((it) => (
            <View key={it.id} style={styles.row} wrap={false}>
              <View style={styles.rowTop}>
                <Text style={styles.cellStatus}>{statusLabel(it.status)}</Text>
                <Text style={styles.cellPriority}>{priorityLabel(it.priority)}</Text>
                <Text style={{ flexGrow: 1, minWidth: 0 }}>
                  {breakLongWords(truncate(it.description || '—', 260))}
                </Text>
                <Text style={styles.cellEst}>
                  {it.estimated_amount != null ? formatCurrency(it.estimated_amount) : '—'}
                </Text>
              </View>
              {it.internal_notes ? (
                <View style={{ marginTop: 3 }}>
                  <Text style={styles.noteText}>Obs.: {breakLongWords(truncate(it.internal_notes, 180))}</Text>
                </View>
              ) : null}
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default VehicleMaintenanceFuturePDF;

